// ============================================================================
// BrightPath - NotificationList Component
// BP-12-03: Notifications Frontend
// ============================================================================

import React, { useMemo } from 'react';
import { format, isToday, isYesterday, isThisWeek } from 'date-fns';
import { Bell, Loader2 } from 'lucide-react';
import { NotificationItem } from './NotificationItem';
import type {
  NotificationListProps,
  Notification,
  NotificationGroup,
} from '../types/notificationsTypes';

// ----------------------------------------------------------------------------
// Helper Functions
// ----------------------------------------------------------------------------

/**
 * Group notifications by date
 */
function groupNotificationsByDate(notifications: Notification[]): NotificationGroup[] {
  const groups: Map<string, Notification[]> = new Map();

  notifications.forEach((notification) => {
    const date = new Date(notification.createdAt);
    let label: string;

    if (isToday(date)) {
      label = 'Today';
    } else if (isYesterday(date)) {
      label = 'Yesterday';
    } else if (isThisWeek(date)) {
      label = format(date, 'EEEE'); // Day name
    } else {
      label = format(date, 'MMMM d, yyyy');
    }

    if (!groups.has(label)) {
      groups.set(label, []);
    }
    groups.get(label)!.push(notification);
  });

  return Array.from(groups.entries()).map(([date, items]) => ({
    date,
    notifications: items,
  }));
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  loading = false,
  error = null,
  onLoadMore,
  hasMore = false,
  onMarkRead,
  onDelete,
  onNotificationClick,
  emptyMessage = 'No notifications yet',
  groupByDate = true,
}) => {
  // Group notifications by date if enabled
  const groupedNotifications = useMemo(() => {
    if (!groupByDate) return null;
    return groupNotificationsByDate(notifications);
  }, [notifications, groupByDate]);

  // Loading state
  if (loading && notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-sage animate-spin mb-3" />
        <p className="text-warm-gray text-sm">Loading notifications...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
          <Bell className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-navy font-medium mb-1">Failed to load notifications</p>
        <p className="text-warm-gray text-sm">{error}</p>
      </div>
    );
  }

  // Empty state
  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 rounded-full bg-cream-dark flex items-center justify-center mb-4">
          <Bell className="w-8 h-8 text-warm-gray" />
        </div>
        <p className="text-navy font-medium mb-1">All caught up!</p>
        <p className="text-warm-gray text-sm text-center max-w-xs">
          {emptyMessage}
        </p>
      </div>
    );
  }

  // Render grouped notifications
  if (groupByDate && groupedNotifications) {
    return (
      <div className="space-y-6">
        {groupedNotifications.map((group) => (
          <div key={group.date}>
            {/* Date header */}
            <div className="sticky top-0 bg-cream/95 backdrop-blur-sm py-2 z-10">
              <h3 className="text-xs font-semibold text-warm-gray uppercase tracking-wider">
                {group.date}
              </h3>
            </div>

            {/* Notifications in group */}
            <div className="space-y-2">
              {group.notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={onMarkRead}
                  onDelete={onDelete}
                  onClick={onNotificationClick}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Load more button */}
        {hasMore && (
          <div className="flex justify-center pt-4">
            <button
              onClick={onLoadMore}
              disabled={loading}
              className="
                px-6 py-2.5 rounded-xl text-sm font-medium
                text-sage hover:text-sage-dark
                bg-sage/10 hover:bg-sage/20
                transition-colors duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center gap-2
              "
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load more'
              )}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Render flat list
  return (
    <div className="space-y-2">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onMarkRead={onMarkRead}
          onDelete={onDelete}
          onClick={onNotificationClick}
        />
      ))}

      {/* Load more button */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="
              px-6 py-2.5 rounded-xl text-sm font-medium
              text-sage hover:text-sage-dark
              bg-sage/10 hover:bg-sage/20
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center gap-2
            "
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Load more'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationList;

